import { useRef } from 'react'

import { useReactiveVar } from '@apollo/client'
import Flicking from '@egjs/react-flicking'

import Clickable from '@web/components/Clickable'
import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import Tooltip from '@web/components/Tooltip/Tooltip'
import { gql } from '@web/gql'
import { Category_ReviewGroupCategoryFragment, UserReviewGroupCategoryType } from '@web/gql/graphql'
import useMe from '@web/hooks/useMe'
import usePageInfo from '@web/hooks/usePageInfo'
import useShowTooltip from '@web/hooks/useShowTooltip'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import nClicks from '@web/libs/nlog/click/codes'
import { ClickEvent } from '@web/types'

import styles from './Category.module.scss'
import { reviewTabFilterStateVar } from '../../filter.state'

const 전체탭_카테고리만_있는경우 = 1

interface Props {
  categories: Category_ReviewGroupCategoryFragment[]
}

const Category = ({ categories }: Props) => {
  const { isMyPage } = usePageInfo()
  const { me } = useMe()

  const placeCategoryId = useReactiveVar(reviewTabFilterStateVar.placeCategoryId)
  const regionCode = useReactiveVar(reviewTabFilterStateVar.regionCode)

  const flickingAreaRef = useRef<Flicking>(null)

  const selectCategoryFilter = (categoryFilter: Category_ReviewGroupCategoryFragment) => {
    const { ReviewGroupPlaceCategory, ReviewGroupRegionCategory } = extractByTypename(categoryFilter)

    if (ReviewGroupPlaceCategory) {
      reviewTabFilterStateVar.placeCategoryId(categoryFilter.code)
      reviewTabFilterStateVar.regionCode(undefined)
      return
    }

    if (ReviewGroupRegionCategory) {
      reviewTabFilterStateVar.regionCode(categoryFilter.code)
      reviewTabFilterStateVar.placeCategoryId(undefined)
      return
    }

    reviewTabFilterStateVar.placeCategoryId(undefined)
    reviewTabFilterStateVar.regionCode(undefined)
  }

  const isSelectedCategoryFilter = (categoryFilter: Category_ReviewGroupCategoryFragment) => {
    const { ReviewGroupPlaceCategory, ReviewGroupRegionCategory, ReviewGroupAllCategory } =
      extractByTypename(categoryFilter)

    return (
      (ReviewGroupPlaceCategory && categoryFilter.code === placeCategoryId) ||
      (ReviewGroupRegionCategory && categoryFilter.code === regionCode) ||
      (ReviewGroupAllCategory && !(placeCategoryId || regionCode))
    )
  }

  const firstMenuCategory = categories?.find(
    category =>
      extractByTypename(category).ReviewGroupPlaceCategory &&
      category.type === UserReviewGroupCategoryType.PlaceCategoryMenu,
  )

  const TOOLTIP_KEY = `reviewtab-category-${isMyPage ? 'my' : 'others'}-${me?.id ?? ''}`
  const TOOLTIP_MAX_SHOW_COUNT = 3
  const { showTooltipLayer, closeTooltip } = useShowTooltip({
    tooltipKey: TOOLTIP_KEY,
    maxShowCount: TOOLTIP_MAX_SHOW_COUNT,
    isShowable: isMyPage ? categories.length > 1 : !!firstMenuCategory,
  })

  const isCategoryFilterWithTooltip = (categoryFilter: Category_ReviewGroupCategoryFragment) => {
    if (isMyPage) return extractByTypename(categoryFilter).ReviewGroupAllCategory
    return categoryFilter.code === firstMenuCategory?.code
  }

  const nClickArea = isMyPage ? nClicks.reviewTab : nClicks.reviewTabOthers
  const nClickCodeMap = {
    [UserReviewGroupCategoryType.All]: nClickArea.FILTERALL,
    [UserReviewGroupCategoryType.PlaceCategoryMain]: nClickArea.FILTERCATEGORY,
    [UserReviewGroupCategoryType.PlaceCategoryMenu]: nClickArea.FILTERMENU,
    [UserReviewGroupCategoryType.RegionSigungu]: nClickArea.FILTERSIGUNGU,
    [UserReviewGroupCategoryType.RegionDongmyun]: nClickArea.FILTERDONG,
  }

  const handleCloseTooltip = (e?: unknown) => {
    const clickEvent = e as ClickEvent
    clickEvent?.stopPropagation?.()
    if (showTooltipLayer) closeTooltip()
  }

  const saveLastFlickingStatus = () => {
    if (flickingAreaRef.current) {
      reviewTabFilterStateVar.flickingStatus(flickingAreaRef.current.getStatus())
    }
  }

  if (categories.length <= 전체탭_카테고리만_있는경우) return null

  return (
    <div className={styles.category_filter_area}>
      <FlickingArea
        arrowOption={{ moveCount: 2, hasGradient: true }}
        options={{
          align: 'prev',
          onClick: handleCloseTooltip,
          onReady: () => {
            const status = reviewTabFilterStateVar.flickingStatus()

            if (status) flickingAreaRef.current?.setStatus(status)
          },
          onMoveEnd: saveLastFlickingStatus,
          onMoveStart: closeTooltip,
          bounce: 0,
        }}
        useReinitialize={false}
        ref={flickingAreaRef}
      >
        {categories.map((categoryFilter, index) => {
          const { code, name, reviewGroupsCount, type } = categoryFilter
          return (
            <span key={`${code}_${index}`} className={styles.btn_wrapper}>
              <span className={styles.inner}>
                <Clickable
                  className={styles.btn}
                  aria-pressed={isSelectedCategoryFilter(categoryFilter)}
                  onClick={() => selectCategoryFilter(categoryFilter)}
                  nClickCode={nClickCodeMap[type]}
                >
                  {name}
                  <span className={styles.count}>{reviewGroupsCount}</span>
                </Clickable>
                {showTooltipLayer && isCategoryFilterWithTooltip(categoryFilter) && (
                  <div className={styles.ly_tooltip_wrapper}>
                    <Tooltip
                      nClickCloseCode={nClickArea.FILTERTOOLTIPX}
                      onClickCloseButton={closeTooltip}
                      hasCloseButton
                      pinPosition="top"
                      pinStyle={{ left: 16 }}
                    >
                      {isMyPage ? (
                        <>
                          <em>리뷰 많이 남긴 지역과 업종</em>을 확인해보세요!
                        </>
                      ) : (
                        <>
                          <em>리뷰 많이 남긴 메뉴</em>를 먼저 보여드려요
                        </>
                      )}
                    </Tooltip>
                  </div>
                )}
              </span>
            </span>
          )
        })}
      </FlickingArea>
    </div>
  )
}

Category.fragments = gql(`
  fragment Category_ReviewGroupCategory on ReviewGroupCategory {
    ... on ReviewGroupAllCategory {
      code
      type
      name
      reviewGroupsCount
    }
    ... on ReviewGroupPlaceCategory {
      code
      type
      name
      reviewGroupsCount
    }
    ... on ReviewGroupRegionCategory {
      code
      type
      name
      reviewGroupsCount
    }
  }
`)

export default Category
